import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Download,
  Mail,
  TrendingUp,
  CheckCircle2,
  Clock,
  Gift,
  Ticket,
  Users,
} from "lucide-react";
import { isStaffLoggedIn, staffHeaders } from "@/lib/staffAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDe = (d) => format(d, "dd.MM.yyyy");

const StaffSchichtbericht = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [staffName, setStaffName] = useState(localStorage.getItem("staff_name") || "");
  const [emailSending, setEmailSending] = useState(false);

  const date = new Date();
  const dateStr = fmtDe(date);

  useEffect(() => {
    if (!isStaffLoggedIn()) navigate("/staff/login", { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/staff/schichtbericht/${dateStr}`, {
        headers: staffHeaders(),
      });
      setData(r.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/staff/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [dateStr, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async () => {
    try {
      const params = staffName ? `?staff=${encodeURIComponent(staffName)}` : "";
      const r = await axios.get(
        `${API}/staff/schichtbericht/${dateStr}/pdf${params}`,
        { headers: staffHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Schichtbericht-${dateStr.replace(/\./g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("PDF konnte nicht erstellt werden");
    }
  };

  const sendEmail = async () => {
    if (staffName) localStorage.setItem("staff_name", staffName);
    setEmailSending(true);
    try {
      await axios.post(
        `${API}/staff/schichtbericht/email`,
        { date: dateStr, staff_name: staffName || null },
        { headers: staffHeaders() }
      );
      toast.success("Schichtbericht wurde per E-Mail versendet");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setEmailSending(false);
    }
  };

  const s = data?.summary;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/staff")}
            className="p-2 -ml-2 text-slate-600 active:bg-slate-100 rounded-lg"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1
              className="text-base font-semibold text-slate-900 leading-tight"
              style={{ fontFamily: "Playfair Display" }}
            >
              Schichtbericht
            </h1>
            <p className="text-xs text-slate-500">
              {format(date, "EEEE, d. MMMM yyyy", { locale: de })}
            </p>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 pb-24 space-y-4">
        {/* Schicht-Name */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <Label htmlFor="staff-name" className="text-xs uppercase tracking-wide text-slate-500">
            Schicht-Mitarbeiter (optional)
          </Label>
          <Input
            id="staff-name"
            value={staffName}
            onChange={(e) => setStaffName(e.target.value)}
            placeholder="Vorname"
            className="mt-1 h-11"
            data-testid="staff-name-input"
          />
        </div>

        {/* Tagesumsatz Highlight */}
        {s && (
          <div className="bg-gradient-to-br from-sky-500 to-blue-600 text-white rounded-2xl p-5 shadow-lg" data-testid="revenue-card">
            <div className="flex items-center gap-2 text-sky-100 text-xs uppercase tracking-wider">
              <TrendingUp className="w-4 h-4" />
              Tagesumsatz (gebucht)
            </div>
            <div className="text-4xl font-bold mt-1" data-testid="revenue-total">
              {s.total_revenue.toFixed(2)} €
            </div>
            <div className="text-sm text-sky-100 mt-2 grid grid-cols-3 gap-2">
              <div>
                <div className="opacity-80">Eisstock</div>
                <div className="font-semibold">{s.eisstock_revenue.toFixed(0)} €</div>
              </div>
              {data.silvester.applies && (
                <div>
                  <div className="opacity-80">Silvester</div>
                  <div className="font-semibold">{s.silvester_revenue.toFixed(0)} €</div>
                </div>
              )}
              <div>
                <div className="opacity-80">Gutschein</div>
                <div className="font-semibold">{s.gutschein_redemption_value.toFixed(0)} €</div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        {s && (
          <div className="grid grid-cols-2 gap-3" data-testid="stats-grid">
            <MiniCard
              icon={<CheckCircle2 className="w-5 h-5" />}
              color="bg-green-50 text-green-700"
              label="Eingecheckt"
              value={s.checked_eisstock_count + s.checked_silvester_count}
            />
            <MiniCard
              icon={<Clock className="w-5 h-5" />}
              color="bg-amber-50 text-amber-700"
              label="Offen"
              value={s.open_eisstock_count + s.open_silvester_count}
            />
            <MiniCard
              icon={<Users className="w-5 h-5" />}
              color="bg-sky-50 text-sky-700"
              label="Eisstock-Sessions"
              value={data?.eisstock?.total_groups ?? 0}
            />
            <MiniCard
              icon={<Gift className="w-5 h-5" />}
              color="bg-rose-50 text-rose-700"
              label="Gutscheine eingelöst"
              value={data?.gutscheine_redeemed?.count ?? 0}
            />
          </div>
        )}

        {/* Silvester (only if applies) */}
        {data?.silvester?.applies && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Ticket className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-semibold text-slate-900">Silvester</h2>
            </div>
            <p className="text-sm text-slate-600">
              {s.checked_silvester_count} eingelassen · {s.open_silvester_count} offen
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3 pt-2">
          <Button
            onClick={downloadPdf}
            disabled={loading || !data}
            className="w-full h-14 bg-slate-900 hover:bg-slate-800 rounded-2xl text-base"
            data-testid="download-pdf-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            PDF speichern
          </Button>
          <Button
            onClick={sendEmail}
            disabled={emailSending || !data}
            className="w-full h-14 bg-sky-500 hover:bg-sky-600 rounded-2xl text-base"
            data-testid="send-email-btn"
          >
            <Mail className="w-5 h-5 mr-2" />
            {emailSending ? "Wird versendet…" : "Schicht beenden & E-Mail senden"}
          </Button>
          <p className="text-center text-xs text-slate-400">
            E-Mail geht an info@groemitzereiszeit.de
          </p>
        </div>
      </main>
    </div>
  );
};

const MiniCard = ({ icon, color, label, value }) => (
  <div className={`rounded-2xl p-4 ${color}`}>
    <div className="flex items-center gap-2 opacity-80">
      {icon}
      <span className="text-xs uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

export default StaffSchichtbericht;
